import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { InventoryTransfer, InventoryTransferItem, Warehouse, InventoryItem } from '../types';
import { formatDate } from '../utils/dateFormat';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

const InventoryTransfers: React.FC = () => {
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState<InventoryTransfer[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState<InventoryTransfer | null>(null);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [selectedJsonData, setSelectedJsonData] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [warehouseStocks, setWarehouseStocks] = useState<Record<string, number>>({});

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    fromWarehouseId: '',
    toWarehouseId: '',
    status: 'DRAFT' as 'DRAFT' | 'POSTED' | 'CANCELLED',
    comment: '',
    items: [] as Partial<InventoryTransferItem>[]
  });

  // Функции пагинации
  const handlePageChange = useCallback((page: number) => {
    fetchTransfers(page);
  }, []);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  }, [currentPage, handlePageChange]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, handlePageChange]);

  useEffect(() => {
    fetchTransfers(1);
    fetchWarehouses();
    fetchInventoryItems();
  }, []);

  // Загружаем остатки при изменении склада-отправителя или даты
  useEffect(() => {
    if (formData.fromWarehouseId && formData.date) {
      fetchWarehouseStocks(formData.fromWarehouseId, formData.date);
    } else {
      setWarehouseStocks({});
    }
  }, [formData.fromWarehouseId, formData.date]);

  const fetchTransfers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const data = await apiGet<{ transfers?: InventoryTransfer[]; pagination?: { pages?: number } }>('/inventory-transfers', {
        page,
        limit: itemsPerPage
      });
      setTransfers(data.transfers || []);
      setTotalPages(data.pagination?.pages || 1);
      setCurrentPage(page);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки перемещений ТМЦ');
      console.error('Ошибка:', err);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);

  const fetchWarehouses = async () => {
    try {
      const data = await apiGet<Warehouse[]>('/warehouses');
      setWarehouses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Ошибка загрузки складов:', err);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const data = await apiGet<{ items?: InventoryItem[] } | InventoryItem[]>('/inventory-items');
      setInventoryItems(Array.isArray(data) ? data : (data.items || []));
    } catch (err) {
      console.error('Ошибка загрузки ТМЦ:', err);
    }
  };

  const fetchWarehouseStocks = async (warehouseId: string, date: string) => {
    if (!warehouseId || !date) return;
    
    try {
      const data = await apiGet<{ balances?: Array<{ inventoryItemId: string; quantity?: number }> }>('/reports/inventory-balances', {
        warehouseId,
        date
      });
      
      const stocks: Record<string, number> = {};
      data.balances?.forEach((balance: any) => {
        stocks[balance.inventoryItemId] = balance.quantity || 0;
      });
      
      setWarehouseStocks(stocks);
    } catch (err) {
      console.error('Ошибка загрузки остатков склада:', err);
      setWarehouseStocks({});
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      fromWarehouseId: '',
      toWarehouseId: '',
      status: 'DRAFT',
      comment: '',
      items: []
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.fromWarehouseId || !formData.toWarehouseId) {
      setError('Необходимо указать склады отправитель и получатель');
      return;
    }

    if (formData.fromWarehouseId === formData.toWarehouseId) {
      setError('Склад отправитель и получатель не могут быть одинаковыми');
      return;
    }

    if (formData.items.length === 0) {
      setError('Необходимо добавить позиции документа');
      return;
    }

    try {
      if (editingTransfer) {
        await apiPut(`/inventory-transfers/${editingTransfer.id}`, formData);
      } else {
        await apiPost('/inventory-transfers', formData);
      }

      await fetchTransfers(currentPage);
      setShowForm(false);
      setEditingTransfer(null);
      resetForm();
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Ошибка сохранения перемещения');
      console.error('Ошибка:', err);
    }
  };

  const handleEdit = (transfer: InventoryTransfer) => {
    setEditingTransfer(transfer);
    setFormData({
      date: transfer.date.split('T')[0],
      fromWarehouseId: transfer.fromWarehouseId,
      toWarehouseId: transfer.toWarehouseId,
      status: transfer.status,
      comment: transfer.comment || '',
      items: transfer.items?.map(item => ({
        inventoryItemId: item.inventoryItemId,
        quantity: item.quantity,
        unit: item.unit,
        comment: item.comment || ''
      })) || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить перемещение ТМЦ?')) return;
    
    try {
      await apiDelete(`/inventory-transfers/${id}`);
      await fetchTransfers(currentPage);
    } catch (err: any) {
      setError(err.message || 'Ошибка удаления перемещения');
      console.error('Ошибка:', err);
    }
  };

  const handleViewJson = (transfer: InventoryTransfer) => {
    const jsonData = JSON.stringify(transfer, null, 2);
    setSelectedJsonData(jsonData);
    setShowJsonModal(true);
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { inventoryItemId: '', quantity: 0, unit: 'шт', comment: '' }]
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index: number, field: keyof InventoryTransferItem, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'POSTED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'Черновик';
      case 'POSTED': return 'Проведен';
      case 'CANCELLED': return 'Отменен';
      default: return status;
    }
  };

  const getItemStock = (inventoryItemId: string) => {
    return warehouseStocks[inventoryItemId] || 0;
  };

  if (loading && transfers.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">🔄 Перемещения ТМЦ</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/purchases')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            📦 Закупка ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-consumptions')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            📦 Расход ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-returns')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            🔄 Возврат ТМЦ
          </button>
          <button
            onClick={() => navigate('/inventory-sales')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            💰 Продажа ТМЦ
          </button>
          <button
            onClick={() => {
              resetForm();
              setEditingTransfer(null);
              setShowForm(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            ➕ Добавить перемещение
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingTransfer ? 'Редактировать перемещение ТМЦ' : 'Добавить перемещение ТМЦ'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Дата *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="DRAFT">Черновик</option>
                    <option value="POSTED">Проведен</option>
                    <option value="CANCELLED">Отменен</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Склад отправитель *</label>
                  <select
                    value={formData.fromWarehouseId}
                    onChange={(e) => setFormData(prev => ({ ...prev, fromWarehouseId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Выберите склад</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Склад получатель *</label>
                  <select
                    value={formData.toWarehouseId}
                    onChange={(e) => setFormData(prev => ({ ...prev, toWarehouseId: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Выберите склад</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Комментарий</label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Позиции документа *
                  </label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                  >
                    Добавить позицию
                  </button>
                </div>
                
                {formData.fromWarehouseId && formData.date && (
                  <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                    📦 Остатки склада-отправителя на {formatDate(formData.date)} будут показаны при выборе товара
                  </div>
                )}
                
                <div className="space-y-2">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-2 p-3 border border-gray-200 rounded">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">ТМЦ *</label>
                        <select
                          value={item.inventoryItemId || ''}
                          onChange={(e) => updateItem(index, 'inventoryItemId', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          required
                        >
                          <option value="">Выберите ТМЦ</option>
                          {inventoryItems.map(invItem => (
                            <option key={invItem.id} value={invItem.id}>
                              {invItem.name} ({invItem.unit})
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Количество *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.quantity || ''}
                          onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          required
                        />
                        {item.inventoryItemId && (
                          <div className="mt-1 text-xs text-gray-500">
                            Остаток: <span className="font-medium">{getItemStock(item.inventoryItemId).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Единица</label>
                        <input
                          type="text"
                          value={item.unit || 'шт'}
                          onChange={(e) => updateItem(index, 'unit', e.target.value)}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div className="flex items-end space-x-1">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Комментарий</label>
                          <input
                            type="text"
                            value={item.comment || ''}
                            onChange={(e) => updateItem(index, 'comment', e.target.value)}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="px-2 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingTransfer(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingTransfer ? 'Обновить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Номер
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Дата
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Откуда
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Куда
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Статус
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Позиций
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Действия
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {transfers.map((transfer) => (
              <tr key={transfer.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {transfer.number || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(transfer.date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {transfer.fromWarehouse?.name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {transfer.toWarehouse?.name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transfer.status)}`}>
                    {getStatusText(transfer.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {transfer.items?.length || 0}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  <button
                    onClick={() => handleEdit(transfer)}
                    className="text-indigo-600 hover:text-indigo-900"
                    title="Редактировать"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleViewJson(transfer)}
                    className="text-blue-600 hover:text-blue-900"
                    title="Просмотр JSON"
                  >
                    📄
                  </button>
                  <button
                    onClick={() => handleDelete(transfer.id)}
                    className="text-red-600 hover:text-red-900"
                    title="Удалить"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Предыдущая
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Следующая
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Показано{' '}
                <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                {' - '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, transfers.length + (currentPage - 1) * itemsPerPage)}
                </span>
                {' из '}
                <span className="font-medium">{totalPages * itemsPerPage}</span>
                {' результатов'}
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Предыдущая</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Номера страниц */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return (
                      <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Следующая</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* JSON Modal */}
      {showJsonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                JSON содержимое документа
              </h3>
              <button
                onClick={() => setShowJsonModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="bg-gray-100 rounded p-4 max-h-96 overflow-auto">
              <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                {selectedJsonData}
              </pre>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowJsonModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryTransfers;
